import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteAdminLabsFeature, UserKeys } from '../../api/users-api';
import { useStore } from '../state/zustand-store';
import { handleErrors } from '../../utils/errors';
import SharedDialog from '../../shared/shared-dialog';
import { Messages } from '../../utils/message-constants';
import { FeatureEntity } from '../../../types';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

export interface LabsDeleteDialogProps {
  open: boolean;
  handleClose: () => void;
  featureNames: string[];
}
export default function LabsDeleteFeatureDialog({
  open,
  handleClose,
  featureNames,
}: LabsDeleteDialogProps) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: (featureNames: string[]) => {
      return deleteAdminLabsFeature(featureNames);
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted(
          featureNames.length > 1 ? 'Features' : 'Feature'
        ),
      });
      queryClient.setQueryData(
        [UserKeys.AdminLabsPage],
        (oldData: FeatureEntity[]) =>
          oldData.filter(
            (feature) => !featureNames.includes(feature.featureString)
          )
      );
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting('Feature'),
      });
    },
  });

  const handleSubmit = () => {
    mutate(featureNames);
  };

  return (
    <SharedDialog
      open={open}
      title={
        featureNames.length > 1
          ? 'Delete Features'
          : `Delete ${featureNames[0]}`
      }
      handleClose={() => {
        handleClose();
      }}
      maxWidth="sm"
      dialogContent={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            flexDirection: 'column',
          }}
        >
          <Typography>
            {`Do you wish to delete the selected ${
              featureNames.length > 1 ? 'features' : 'feature'
            }?`}
          </Typography>
        </Box>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit,
        disabled: isPending,
        title: 'Delete',
        color: 'error',
      }}
    />
  );
}
